// UserBbsComments.js
import React, {useEffect, useState} from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import { Button, IconButton } from '@mui/material';
import InputUtil from '../../../value/InputUtil';
import VzmTextareaField from '../../../common/VzmTextareaField';
import { useConfirmDialog } from '../../../common/AppConfirmDialog';
import AddCommentIcon from '@mui/icons-material/AddComment';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { userBbsCommentListRepo, userInfoRepo } from '../../../value/AppModel';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import {
    LIST_USER_BBS_COMMENTS, ADD_USER_BBS_COMMENTS, EDIT_USER_BBS_COMMENTS, REMOVE_USER_BBS_COMMENTS
} from './UserBbsInfoGql';
import { useAlertSetter } from '../../../api/app-hook';
import { AppObject, AppStyle, AppWord, InputType } from '../../../value/AppConst';

const CommentInputMap = {
    cmtId : {
        id: 'cmtId', required: true, toAdd: false, toEdit: true,
    },
    bbsInfoSeq : {
        id: 'bbsInfoSeq', required: true, toAdd: true, toEdit: true,
    },
    content: {
        id: 'content', label: '댓글', type: InputType.text, required: false,
        toAdd: true, toEdit: true,
        help: '300자 이내로 입력하세요.',
        min: 1, max: 1000,
        rows: 3,
    }
}

const MARGIN_BACK = 18;

const CommentContainer = styled(Box)({
    flexGrow: 1, marginTop: MARGIN_BACK, // height: '100%',
    border: '1px solid #ddd', borderRadius: 5, padding: 10,
});
// CommentContainer 상단의 border에 걸쳐 표시할 제목
const CommentBoxTitle = styled(Box)({
    fontSize:'0.8rem', color: AppStyle.PrimaryRGB, marginTop: -MARGIN_BACK, marginLeft: 10,
    padding: '0px 10px 0px 10px',
});

const InputBox = styled(Box)({
    display: 'flex', alignItems: 'center', marginBottom: 10,
    border: AppStyle.BorderCCC, borderRadius: 5, padding: 5
});
const TextFieldBox = styled(Box)({flexGrow: 1, minWidth: 300, marginRight: 10});

// comment, who, when
const CommentRecord = styled(Box)({
    padding: 10, borderBottom: AppStyle.BorderCCC,
    '&:last-of-type': {borderBottom: 'none'}
});
const CommentContent = styled(Box)({flexGrow: 1, fontSize: '0.9rem', whiteSpace: 'pre-line'});
const CommentWhoAndWhen = styled(Box)({display: 'flex', fontSize: '0.8rem'});
const CommentWho = styled(Box)({color: '#66f'});
const CommentWhen = styled(Box)({color: '#777', flexGrow: 1, textAlign: 'right'});

/*
export const LIST_USER_BBS_COMMENTS = gql`
${USER_BBS_COMMENTS_PART}
query ListUserBbsComments($bbsInfoSeq:Int!) {
    userBbsCommentsList(bbsInfoSeq:$bbsInfoSeq) {
        ...UserBbsCommentsPart
    }
}
`;
*/


/**
 * 사용자 게시판 댓글 목록 + 입력/수정
 * View + Controller
 * @param {*} param0 
 * @returns 
 */
export default function UserBbsComments({
    userBbsInfo,
}) {
    const userBbsCommentListData = useReactiveVar(userBbsCommentListRepo); // {when:0, records:[]}
    const [cmtIdHovered, setCmtIdHovered] = useState(null);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [showInput, setShowInput] = useState(false);
    const sessionInfo = useReactiveVar(userInfoRepo);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();

    // ##### Call GraphQL to get List #####
    const [getUserBbsCommentsList, responseList] = useLazyQuery(LIST_USER_BBS_COMMENTS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
        onError: (error) => addAlert({title: '댓글목록 조회 오류', message: [error.message]})
    });
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addUserBbsComments, responseAdd] = useMutation( ADD_USER_BBS_COMMENTS, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
        onError: (error) => addAlert({title: '댓글 등록 오류', message: [error.message]})
	} );
    const [editUserBbsComments, responseEdit] = useMutation( EDIT_USER_BBS_COMMENTS, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
        onError: (error) => addAlert({title: '댓글 수정 오류', message: [error.message]})
	} );
    const [removeUserBbsComments, responseRemove] = useMutation( REMOVE_USER_BBS_COMMENTS, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
        onError: (error) => addAlert({title: '댓글 삭제 오류', message: [error.message]})
	});

    const clearInput = () => {
        setInput({
            data: {content: '', bbsInfoSeq: userBbsInfo.bbsInfoSeq},
            error: {content: false},
            changed: false,
            hasError: false,
        });
        setIsEdit(false);
        setShowInput(false);
    };

    useEffect(()=>{
        if(userBbsInfo) {
            clearInput();
            getUserBbsCommentsList({variables:{bbsInfoSeq:userBbsInfo.bbsInfoSeq}});
        }
    }, [userBbsInfo]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        // find current itemSelected and reset.
        if(data.userBbsCommentsList) {
            userBbsCommentListRepo({when:new Date().getTime()/1000, records:data.userBbsCommentsList});
            clearInput();
        }
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.userBbsCommentsAdd.ok) {
            userBbsCommentListRepo({when:new Date().getTime()/1000, records:data.userBbsCommentsAdd.list});
            clearInput();
        }
        else
            addAlert({title: '댓글 등록 오류', message: [data.userBbsCommentsAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.userBbsCommentsEdit.ok) {
            userBbsCommentListRepo({when:new Date().getTime()/1000, records:data.userBbsCommentsEdit.list});
            clearInput();
        }
        else
            addAlert({title: '댓글 수정 오류', message: [data.userBbsCommentsEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.userBbsCommentsRemove.ok) {
            userBbsCommentListRepo({when:new Date().getTime()/1000, records:data.userBbsCommentsRemove.list});
            clearInput();
        }
        else
            addAlert({title: '댓글 삭제 오류', message: [data.userBbsCommentsRemove.message]});
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, CommentInputMap, isEdit);
        setInput(newInput);
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: '댓글 삭제',
            content: ['정말 삭제하시겠습니까?'],
        });
        if(confirm)
            removeUserBbsComments({variables:{userBbsComments:{bbsInfoSeq:item.bbsInfoSeq, cmtId:item.cmtId}}});
    };

    const onClickRemove = (item) => {
        confirmRemove(item);
    };

    const onClickEdit = (item) => {
        const newInput = {
            data: {
                bbsInfoSeq: item.bbsInfoSeq,
                cmtId: item.cmtId,
                content: item.content,
            },
            error: {content: false},
            changed: false,
            hasError: false,
        };
        setInput(newInput);
        setIsEdit(true);
    };

    const onClickSave = () => {
        // check isEdit.
        const param = InputUtil.getDataToSubmit(input.data, CommentInputMap, isEdit);
        if(isEdit) editUserBbsComments({variables:{userBbsComments:param}});
        else addUserBbsComments({variables:{userBbsComments:param}});
    };

    const confirmCancel = async () => {
        const confirm = await openConfirmDialog({
            title: '댓글 입력 취소',
            content: ['입력 중인 댓글을 취소하시겠습니까?'],
        });
        if(confirm) clearInput();
    };

    const onClickCancel = () => {
        if(input.changed) confirmCancel();
        else clearInput();
    };

    const loadingAny
    = responseList.loading
    || responseAdd.loading
    || responseEdit.loading
    || responseRemove.loading;

    return(
        <CommentContainer>
            <CommentBoxTitle>
                <span style={{backgroundColor: 'white', padding: '0px 5px 0px 5px'}}>
                    댓글
                </span>
            </CommentBoxTitle>
            {
                showInput ?
                <InputBox>
                    <TextFieldBox>
                        <VzmTextareaField
                            dict={CommentInputMap.content}
                            value={input.data.content}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </TextFieldBox>
                    <Box>
                        <Box style={{marginBottom: 5}}>
                            <Button
                                color={AppStyle.mui.primary}
                                variant={AppStyle.mui.contained}
                                startIcon={<DoneIcon />}
                                onClick={onClickSave}
                                disabled={input.hasError || !input.changed || loadingAny}
                            >
                                {isEdit ? '수정' : '등록'}
                            </Button>
                        </Box>
                        <Box>
                            <Button
                                color={AppStyle.mui.secondary}
                                variant={AppStyle.mui.contained}
                                onClick={onClickCancel}
                                startIcon={<ClearIcon />}
                                disabled={loadingAny}
                            >
                                취소
                            </Button>
                        </Box>
                    </Box>
                </InputBox>
                :
                <div style={{textAlign:'right'}}>
                    <Button
                        onClick={()=>setShowInput(!showInput)}
                        disabled={loadingAny || showInput}
                        size={AppWord.SMALL}
                        color={AppStyle.mui.primary}
                        variant={AppStyle.mui.contained}
                        startIcon={<AddCommentIcon />}
                    >
                        댓글 추가
                    </Button>
                </div>
            }
            {
                userBbsCommentListData.records.map((item, idx) => {
                    return(
                        <CommentRecord key={idx}
                            onMouseEnter={()=>setCmtIdHovered(item.cmtId)}
                            onMouseLeave={()=>setCmtIdHovered(null)}
                        >
                            <CommentContent>
                                <span
                                    style={{color: isEdit && item.cmtId === input.data.cmtId ? '#aaa' : '#000'}}
                                >
                                    {item.content}
                                </span>
                                {
                                    item.cmtId === cmtIdHovered && item.regUserId === sessionInfo.user.userId
                                    ?
                                    <>
                                    <IconButton
                                        onClick={()=>{onClickEdit(item)}}
                                        size={AppWord.SMALL}
                                        style={{marginLeft: 5, float: 'right'}}
                                        color={AppStyle.mui.secondary}
                                        disabled={loadingAny}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={()=>{onClickRemove(item)}}
                                        size={AppWord.SMALL}
                                        style={{marginLeft: 5, float: 'right'}}
                                        color={AppStyle.mui.warning}
                                        disabled={loadingAny}
                                    >
                                        <DeleteForeverIcon />
                                    </IconButton>
                                    </>
                                    : null
                                }
                            </CommentContent>
                            <CommentWhoAndWhen>
                                <CommentWho>{item.regUserName}</CommentWho>
                                <CommentWhen>
                                    {
                                        item.cmtId === cmtIdHovered && item.regUserId === sessionInfo.user.userId
                                        ?
                                        item.regTime.substring(0, 10)
                                        : item.regTime
                                    }
                                </CommentWhen>
                            </CommentWhoAndWhen>
                        </CommentRecord>
                    );
                })
            }
        </CommentContainer>
    );
}